import { useState } from "react";
import { BROWSERS, type BrowserId } from "@/desktop/browser-connection";
import {
  cancelExtensionSetup,
  extensionSetupLabel,
  isExtensionSetupEnd,
  startExtensionSetup,
  useBrowserExtensionSetup,
} from "@/desktop/browser-extension-setup";
import {
  connectBrowserExtension,
  disconnectBrowserExtension,
  type BrowserExtension,
  type BrowserExtensionStates,
} from "@/desktop/denker-browser";
import { useT } from "@/i18n";
import { settingsBrowserMessages } from "@/i18n/messages/settings-browser";
import type { ConnectionPhase } from "./browser-connection-chip";

export type ExtensionConnection = {
  phase: ConnectionPhase;
  /** The row's status words: "Connected", "Extension is off", a step. */
  label: string;
  /** After the chip's name in its tooltip: what a click does, or why not. */
  does: string;
  activate?: () => void;
  reconnect: () => void;
  disconnect: () => void;
  /** Why the last setup ended without connecting, or a request that failed. */
  message: { text: string; alert: boolean } | null;
};

/**
 * The Denker extension in one browser - `extension` undefined while its
 * state is unknown, `loaded` once a read came back - as one of the chip's
 * phases. Connect runs the Chrome Web Store setup that runs natively and is
 * presented by the owner webview (`browser-extension-setup-owner`); after a
 * Disconnect it uses the installed extension again instead. `refresh`
 * re-reads every browser's state.
 */
export function useExtensionConnection(
  browserId: BrowserId,
  extension: BrowserExtension | undefined,
  loaded: boolean,
  refresh: () => Promise<BrowserExtensionStates | null>,
): ExtensionConnection {
  const t = useT(settingsBrowserMessages);
  const progress = useBrowserExtensionSetup((state) => state.progress);
  const [pending, setPending] = useState<"connect" | "disconnect" | null>(null);
  const [error, setError] = useState<string | null>(null);
  // The Denker browser's setup runs this browser too, but is its own row's.
  const mine =
    progress && !progress.denkerBrowser && progress.browserId === browserId
      ? progress
      : null;
  const running = mine && !isExtensionSetupEnd(mine.step) ? mine : null;
  const otherRunning =
    !!progress && !mine && !isExtensionSetupEnd(progress.step);
  const failed =
    !!mine &&
    isExtensionSetupEnd(mine.step) &&
    !["connected", "cancelled"].includes(mine.step) &&
    extension?.state !== "connected";
  const setUp = () => {
    setError(null);
    void startExtensionSetup(browserId).catch(() => {});
  };
  const request = (kind: "connect" | "disconnect") => {
    setError(null);
    setPending(kind);
    void (async () => {
      let answered = true;
      if (kind === "disconnect") await disconnectBrowserExtension(browserId);
      else answered = await connectBrowserExtension(browserId);
      const next = (await refresh())?.[browserId];
      // Connect after a Disconnect, with the extension gone or turned off
      // since: its setup brings it back.
      if (
        kind === "connect" &&
        next?.setup &&
        (next.state === "missing" ||
          next.state === "disabled" ||
          next.state === "turned_off")
      )
        setUp();
      else if (!answered) {
        setError(
          t("errorExtensionNoAnswer", { browser: BROWSERS[browserId].name }),
        );
      }
    })()
      .catch(() => setError(t("errorExtensionUnreachable")))
      .finally(() => setPending(null));
  };
  const reconnect = () => request("connect");
  const disconnect = () => request("disconnect");
  const message = error
    ? { text: error, alert: true }
    : failed && mine
      ? { text: extensionSetupLabel(mine.step, browserId), alert: false }
      : null;
  const base = { reconnect, disconnect, message };
  if (running)
    return {
      ...base,
      phase: "working",
      label: extensionSetupLabel(running.step, browserId),
      does: t("doesStop"),
      activate: () =>
        void cancelExtensionSetup(running.setupId).catch(() => {}),
    };
  if (pending)
    return {
      ...base,
      phase: "working",
      label:
        pending === "disconnect"
          ? t("statusDisconnecting")
          : t("statusConnecting"),
      does:
        pending === "disconnect" ? t("doesDisconnecting") : t("doesConnecting"),
    };
  if (!extension)
    return {
      ...base,
      phase: "unavailable",
      label: loaded ? t("statusExtensionNotConnected") : t("statusChecking"),
      does: t("doesNotAvailableHere"),
    };
  if (extension.state === "connected")
    return {
      ...base,
      phase: "connected",
      label: t("statusConnected"),
      does: t("doesConnected"),
    };
  if (extension.state === "disconnected")
    return {
      ...base,
      phase: "not_connected",
      label: t("statusDisconnected"),
      does: t("doesConnect"),
      activate: reconnect,
    };
  // Connected but too old: the browser updates it on its own, and a restart
  // applies a waiting update.
  if (extension.state === "outdated")
    return {
      ...base,
      phase: "needs_you",
      label: t("statusExtensionOutdated"),
      does: t("doesUpdatesItself"),
    };
  // Connected, not agreed yet: the setup brings the welcome page up and waits.
  if (extension.state === "consent")
    return {
      ...base,
      phase: "needs_you",
      label: t("statusWaitingConsent"),
      does: !extension.setup
        ? t("doesAgreeInBrowser")
        : otherRunning
          ? t("whyOtherSetup")
          : t("doesShowWelcome"),
      activate: extension.setup && !otherRunning ? setUp : undefined,
    };
  // Off - by the browser, or the person - is theirs to turn back on.
  const off =
    extension.state === "disabled" || extension.state === "turned_off";
  const label =
    extension.state === "disabled"
      ? t("statusExtensionOff")
      : extension.state === "turned_off"
        ? t("statusTurnedOff")
        : extension.state === "missing"
          ? t("statusExtensionNotAdded")
          : t("statusExtensionNotConnected");
  const unavailable = !extension.setup
    ? t("whyNoSetup")
    : otherRunning
      ? t("whyOtherSetup")
      : null;
  if (unavailable)
    return { ...base, phase: "unavailable", label, does: unavailable };
  return {
    ...base,
    phase: off ? "needs_you" : "not_connected",
    label,
    does: off ? t("doesTurnOn") : t("doesConnect"),
    activate: setUp,
  };
}
